import React, { useState, useEffect } from 'react';
import { usePage } from '@inertiajs/react';
import { createPortal } from 'react-dom';

interface Flash {
  alert?: string;
  notice?: string;
  key?: string;
}

interface PageProps {
  flash: Flash;
  [key: string]: any;
}

const FlashMessages: React.FC = () => {
  const { flash } = usePage<PageProps>().props;
  const [visible, setVisible] = useState(false);
  
  // Reset visibility when flash messages change
  useEffect(() => {
    if (flash.notice || flash.alert) {
      setVisible(true);
      
      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setVisible(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [flash.notice, flash.alert, flash.key]);

  if (!visible || (!flash.notice && !flash.alert)) {
    return null;
  }
  
  const flashContent = (
    <div className="fixed top-4 right-4 left-4 md:left-auto md:w-1/3" style={{ zIndex: 999999 }}>
      {flash.notice && (
        <div className="bg-green-100 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 text-green-800 dark:text-green-400 px-4 py-3 rounded-lg shadow-md relative mb-2 flex justify-between items-center">
          <div>{flash.notice}</div>
          <button
            onClick={() => setVisible(false)}
            className="text-green-600 hover:text-green-800 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
      
      {flash.alert && (
        <div className="bg-red-100 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-800 dark:text-red-400 px-4 py-3 rounded-lg shadow-md relative mb-2 flex justify-between items-center">
          <div>{flash.alert}</div>
          <button
            onClick={() => setVisible(false)}
            className="text-red-600 hover:text-red-800 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );

  // Use createPortal to render at document body level
  return createPortal(flashContent, document.body);
};

export default FlashMessages;